import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import styles from "./Survey.module.css";
import { capture } from "../../analytics";
import SURVEY_COPY from "./surveyCopy";

// Google Apps Script endpoint from feedback_survey_clear.html (v8)
const SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbwn4n2-jwlcfHEVL0jwvAZoUj891OJD8blCyy4kKjs8ePis9iRZeFh99-MkG7lswzGiMg/exec";

// Per-language storage keys so EN and RU drafts don't collide.
const DRAFT_KEYS = { en: "mm_survey_draft_v8", ru: "mm_survey_draft_v8_ru" };
const SCROLL_KEYS = { en: "mm_survey_scroll_pos", ru: "mm_survey_scroll_pos_ru" };

const INITIAL_VALUES = {
  email: "",
  role: "",
  role_other: "",
  studio_name: "",
  q1_pipeline: [],
  q1_pipeline_other: "",
  q2_pain_point: "",
  q3_camdb_use: "",
  q4_manual_fix: "",
  q4_fix_details: "",
  q5_grid_cost: "",
  q6_justified: "",
  q7_user_tier: "",
  q8_lenses_pref: "",
  q9_subscribe_convince: "",
  q10_subscribe_likelihood: "",
  q11_nps: "",
  q12_free_comment: "",
  consent: false,
  company_hp: "",
};

const REQUIRED_FIELDS = [
  "email",
  "role",
  "q1_pipeline",
  "q2_pain_point",
  "q3_camdb_use",
  "q4_manual_fix",
  "q5_grid_cost",
  "q6_justified",
  "q7_user_tier",
  "q8_lenses_pref",
  "q9_subscribe_convince",
  "q10_subscribe_likelihood",
  "q11_nps",
  "q12_free_comment",
  "consent",
];

const isFieldFilled = (name, values) => {
  const value = values[name];
  if (name === "consent") return Boolean(value);
  if (name === "q1_pipeline") return Array.isArray(value) && value.length > 0;
  if (typeof value === "string") return value.trim() !== "";
  return Boolean(value);
};

const getUnfilledFields = (values) =>
  REQUIRED_FIELDS.filter((name) => !isFieldFilled(name, values));

const Survey = ({ lang = "en" }) => {
  const t = SURVEY_COPY[lang] || SURVEY_COPY.en;
  const draftKey = DRAFT_KEYS[lang] || DRAFT_KEYS.en;
  const scrollKey = SCROLL_KEYS[lang] || SCROLL_KEYS.en;

  const [formValues, setFormValues] = useState(INITIAL_VALUES);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusColor, setStatusColor] = useState("");
  const [errorFields, setErrorFields] = useState([]);
  const [clearConfirming, setClearConfirming] = useState(false);
  const [draftReady, setDraftReady] = useState(false);

  const fieldRefs = useRef({});
  const clearTimerRef = useRef(null);

  const showRoleOther = formValues.role === "Other";
  const showQ1Other = (formValues.q1_pipeline || []).includes("Other");
  const showQ4Conditional =
    formValues.q4_manual_fix === "Yes, often" ||
    formValues.q4_manual_fix === "Sometimes";

  const progressPct = useMemo(() => {
    const unfilled = getUnfilledFields(formValues);
    const filledCount = REQUIRED_FIELDS.length - unfilled.length;
    const pct = Math.round((filledCount / REQUIRED_FIELDS.length) * 100);
    return pct === 0 ? 1 : pct;
  }, [formValues]);

  const isComplete = progressPct === 100;

  // Restore draft once on mount (or when switching language route)
  useEffect(() => {
    setDraftReady(false);
    setFormValues(INITIAL_VALUES);
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const saved = JSON.parse(raw);
        setFormValues((prev) => ({
          ...prev,
          ...saved,
          q1_pipeline: Array.isArray(saved.q1_pipeline)
            ? saved.q1_pipeline
            : prev.q1_pipeline,
          consent: Boolean(saved.consent),
        }));
      }
    } catch (e) {
      // ignore corrupt draft
    }
    setDraftReady(true);

    try {
      const savedScroll = sessionStorage.getItem(scrollKey);
      if (savedScroll) {
        const y = parseInt(savedScroll, 10);
        if (!Number.isNaN(y)) {
          setTimeout(() => window.scrollTo(0, y), 100);
        }
      }
    } catch (e) {
      // ignore
    }
  }, [draftKey, scrollKey]);

  // Persist draft
  useEffect(() => {
    if (!draftReady || submitted) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify(formValues));
    } catch (e) {
      // ignore quota
    }
  }, [formValues, draftReady, submitted, draftKey]);

  // Save scroll position lightly
  useEffect(() => {
    let timer = null;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          sessionStorage.setItem(scrollKey, String(window.scrollY || 0));
        } catch (e) {
          // ignore
        }
      }, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, [scrollKey]);

  useEffect(
    () => () => {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    },
    [],
  );

  const setFieldRef = useCallback((name, el) => {
    if (el) fieldRefs.current[name] = el;
  }, []);

  const updateValue = useCallback((name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrorFields((prev) => prev.filter((f) => f !== name));
    setStatusMessage("");
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" && name === "q1_pipeline") {
      setFormValues((prev) => {
        const current = Array.isArray(prev.q1_pipeline) ? [...prev.q1_pipeline] : [];
        if (checked) {
          if (!current.includes(value)) current.push(value);
        } else {
          const idx = current.indexOf(value);
          if (idx > -1) current.splice(idx, 1);
        }
        const next = { ...prev, q1_pipeline: current };
        if (!current.includes("Other")) next.q1_pipeline_other = "";
        return next;
      });
      setErrorFields((prev) => prev.filter((f) => f !== "q1_pipeline"));
      setStatusMessage("");
      return;
    }

    if (type === "checkbox" && name === "consent") {
      updateValue("consent", checked);
      return;
    }

    if (name === "role") {
      setFormValues((prev) => ({
        ...prev,
        role: value,
        role_other: value === "Other" ? prev.role_other : "",
      }));
      setErrorFields((prev) => prev.filter((f) => f !== "role"));
      setStatusMessage("");
      return;
    }

    if (name === "q4_manual_fix") {
      setFormValues((prev) => ({
        ...prev,
        q4_manual_fix: value,
        q4_fix_details:
          value === "Yes, often" || value === "Sometimes"
            ? prev.q4_fix_details
            : "",
      }));
      setErrorFields((prev) => prev.filter((f) => f !== "q4_manual_fix"));
      setStatusMessage("");
      return;
    }

    updateValue(name, value);
  };

  const handleClear = () => {
    if (clearConfirming) {
      setFormValues(INITIAL_VALUES);
      setErrorFields([]);
      setStatusMessage("");
      setStatusColor("");
      setClearConfirming(false);
      try {
        localStorage.removeItem(draftKey);
      } catch (e) {
        // ignore
      }
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      return;
    }

    setClearConfirming(true);
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => {
      setClearConfirming(false);
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot
    if (formValues.company_hp) return;

    const unfilled = getUnfilledFields(formValues);
    if (unfilled.length > 0) {
      setErrorFields(unfilled);
      setStatusMessage(t.validationError);
      setStatusColor("#ff4d4d");
      const first = unfilled[0];
      const el = fieldRefs.current[first];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      capture("survey_validation_failed", {
        lang,
        missing_count: unfilled.length,
        missing_fields: unfilled,
      });
      return;
    }

    setIsSubmitting(true);
    setStatusMessage("");
    setErrorFields([]);

    const payload = {
      ...formValues,
      q1_pipeline: Array.isArray(formValues.q1_pipeline)
        ? formValues.q1_pipeline
        : [],
    };

    try {
      const res = await fetch(SCRIPT_URL, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "text/plain" },
      });

      if (!res.ok) throw new Error("API Error");

      setSubmitted(true);
      setStatusMessage(t.success);
      setStatusColor("var(--color-accent-1)");
      setFormValues(INITIAL_VALUES);
      try {
        localStorage.removeItem(draftKey);
        sessionStorage.removeItem(scrollKey);
      } catch (err) {
        // ignore
      }

      // Analytics: success without PII (email stripped by sanitizeEventProps too)
      capture("survey_submitted", {
        lang,
        role: formValues.role,
        has_studio: Boolean(formValues.studio_name?.trim()),
        q1_pipeline_count: Array.isArray(formValues.q1_pipeline)
          ? formValues.q1_pipeline.length
          : 0,
        q3_camdb_use: formValues.q3_camdb_use,
        q4_manual_fix: formValues.q4_manual_fix,
        q5_grid_cost: formValues.q5_grid_cost,
        q7_user_tier: formValues.q7_user_tier,
        q8_lenses_pref: formValues.q8_lenses_pref,
        q10_subscribe_likelihood: formValues.q10_subscribe_likelihood,
        q11_nps: formValues.q11_nps,
      });
    } catch (err) {
      setStatusMessage(t.submitFailed);
      setStatusColor("#e57373");
      capture("survey_submit_failed", {
        lang,
        error_message: err?.message || "unknown",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const cardClass = (fieldName) =>
    `${styles.card}${errorFields.includes(fieldName) ? ` ${styles.cardError}` : ""}`;

  const progressTextTop = `calc(${100 - progressPct}% - 12px)`;

  return (
    <div className={styles.survey}>
      <Helmet>
        <html lang={t.meta.htmlLang} />
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
      </Helmet>

      <div className={styles.progressContainer} aria-hidden="true">
        <div
          className={`${styles.progressBar}${isComplete ? ` ${styles.progressBarComplete}` : ""}`}
          style={{ height: `${progressPct}%` }}
        />
        <div
          className={`${styles.progressText}${isComplete ? ` ${styles.progressTextComplete}` : ""}`}
          style={{ top: progressTextTop }}
        >
          {progressPct}%
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.header}>
          <h1>{t.header.h1}</h1>
          <p className={styles.subtitle}>{t.header.subtitle}</p>
          <div className={styles.giftBadge}>{t.header.giftBadge}</div>
        </div>

        {submitted ? (
          <div
            className={styles.success}
            dangerouslySetInnerHTML={{ __html: statusMessage }}
          />
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <input
              type="text"
              name="company_hp"
              className={styles.hp}
              value={formValues.company_hp}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <div className={styles.sectionTitle}>{t.sections.about}</div>

            <div
              className={cardClass("email")}
              ref={(el) => setFieldRef("email", el)}
            >
              <label className={styles.qLabel} htmlFor="survey-email">
                {t.email.label}
              </label>
              <input
                id="survey-email"
                type="email"
                name="email"
                placeholder={t.email.placeholder}
                value={formValues.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>

            <div
              className={cardClass("role")}
              ref={(el) => setFieldRef("role", el)}
            >
              <div className={styles.fieldGap}>
                <label className={styles.qLabel} htmlFor="survey-role">
                  {t.role.label}
                </label>
                <select
                  id="survey-role"
                  name="role"
                  value={formValues.role}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    {t.role.selectPlaceholder}
                  </option>
                  {t.role.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div
                  className={`${styles.conditional}${showRoleOther ? ` ${styles.conditionalShow}` : ""}`}
                >
                  <input
                    type="text"
                    name="role_other"
                    placeholder={t.role.otherPlaceholder}
                    value={formValues.role_other}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label className={styles.qLabel} htmlFor="survey-studio">
                  {t.studio.label}
                </label>
                <input
                  id="survey-studio"
                  type="text"
                  name="studio_name"
                  placeholder={t.studio.placeholder}
                  value={formValues.studio_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={styles.sectionTitle}>{t.sections.pipeline}</div>

            <div
              className={cardClass("q1_pipeline")}
              ref={(el) => setFieldRef("q1_pipeline", el)}
            >
              <label className={styles.qLabel}>{t.q1.label}</label>
              <div className={styles.options}>
                {t.q1.options.map((opt) => (
                  <label key={opt.value}>
                    <input
                      type="checkbox"
                      name="q1_pipeline"
                      value={opt.value}
                      checked={(formValues.q1_pipeline || []).includes(opt.value)}
                      onChange={handleChange}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
              <div
                className={`${styles.conditional}${showQ1Other ? ` ${styles.conditionalShow}` : ""}`}
              >
                <input
                  type="text"
                  name="q1_pipeline_other"
                  placeholder={t.q1.otherPlaceholder}
                  value={formValues.q1_pipeline_other}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div
              className={cardClass("q2_pain_point")}
              ref={(el) => setFieldRef("q2_pain_point", el)}
            >
              <label className={styles.qLabel} htmlFor="survey-q2">
                {t.q2.label}
              </label>
              <textarea
                id="survey-q2"
                name="q2_pain_point"
                placeholder={t.q2.placeholder}
                value={formValues.q2_pain_point}
                onChange={handleChange}
              />
            </div>

            <div
              className={cardClass("q3_camdb_use")}
              ref={(el) => setFieldRef("q3_camdb_use", el)}
            >
              <label className={styles.qLabel}>
                {t.q3.before}
                <a
                  href="https://camdb.matchmovemachine.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.camdbLink}
                >
                  {t.q3.link}
                </a>
                {t.q3.after}
              </label>
              <div className={styles.options}>
                {t.q3.options.map((opt) => (
                  <label key={opt.value}>
                    <input
                      type="radio"
                      name="q3_camdb_use"
                      value={opt.value}
                      checked={formValues.q3_camdb_use === opt.value}
                      onChange={handleChange}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div
              className={cardClass("q4_manual_fix")}
              ref={(el) => setFieldRef("q4_manual_fix", el)}
            >
              <label className={styles.qLabel}>{t.q4.label}</label>
              <div className={styles.options}>
                {t.q4.options.map((opt) => (
                  <label key={opt.value}>
                    <input
                      type="radio"
                      name="q4_manual_fix"
                      value={opt.value}
                      checked={formValues.q4_manual_fix === opt.value}
                      onChange={handleChange}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
              <div
                className={`${styles.conditional}${showQ4Conditional ? ` ${styles.conditionalShow}` : ""}`}
              >
                <textarea
                  name="q4_fix_details"
                  placeholder={t.q4.detailPlaceholder}
                  value={formValues.q4_fix_details}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={styles.sectionTitle}>{t.sections.cost}</div>

            <div
              className={`${cardClass("q5_grid_cost")} ${styles.costIntro}`}
              ref={(el) => setFieldRef("q5_grid_cost", el)}
            >
              <h3>{t.cost.h3}</h3>
              <p className={styles.costIntroLead}>
                {t.cost.leadBefore}
                <Link to="/grid-cost-calculator">{t.cost.leadLink}</Link>
                {t.cost.leadAfter}
              </p>

              <div className={styles.costCallout}>
                <div className={styles.costCalloutIcon}>💡</div>
                <p>
                  {t.cost.callout.before1}
                  <strong>{t.cost.callout.price1}</strong>
                  {t.cost.callout.before2}
                  <strong>{t.cost.callout.price2}</strong>
                  {t.cost.callout.before3}
                  <strong className={styles.priceAccent}>
                    {t.cost.callout.price3}
                  </strong>
                  {t.cost.callout.after}
                </p>
              </div>

              <label className={styles.qLabel} style={{ textAlign: "left" }}>
                {t.cost.q5Label}
              </label>
              <div className={styles.options} style={{ textAlign: "left" }}>
                {t.cost.q5Options.map((opt) => (
                  <label key={opt.value}>
                    <input
                      type="radio"
                      name="q5_grid_cost"
                      value={opt.value}
                      checked={formValues.q5_grid_cost === opt.value}
                      onChange={handleChange}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div
              className={cardClass("q6_justified")}
              ref={(el) => setFieldRef("q6_justified", el)}
            >
              <label className={styles.qLabel} htmlFor="survey-q6">
                {t.q6.label}
              </label>
              <textarea
                id="survey-q6"
                name="q6_justified"
                placeholder={t.q6.placeholder}
                value={formValues.q6_justified}
                onChange={handleChange}
              />
            </div>

            <div className={styles.sectionTitle}>{t.sections.subscription}</div>

            <div
              className={cardClass("q7_user_tier")}
              ref={(el) => setFieldRef("q7_user_tier", el)}
            >
              <label className={styles.qLabel}>{t.q7.label}</label>
              <div className={styles.options}>
                {t.q7.options.map((item) => (
                  <label key={item.value} className={styles.optionRich}>
                    <input
                      type="radio"
                      name="q7_user_tier"
                      value={item.value}
                      checked={formValues.q7_user_tier === item.value}
                      onChange={handleChange}
                    />
                    <span>
                      <span className={styles.optionTitle}>{item.title}</span>
                      <span className={styles.optionDesc}>{item.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div
              className={cardClass("q8_lenses_pref")}
              ref={(el) => setFieldRef("q8_lenses_pref", el)}
            >
              <label className={styles.qLabel}>{t.q8.label}</label>
              <div className={styles.options}>
                {t.q8.options.map((item) => (
                  <label key={item.value} className={styles.optionRich}>
                    <input
                      type="radio"
                      name="q8_lenses_pref"
                      value={item.value}
                      checked={formValues.q8_lenses_pref === item.value}
                      onChange={handleChange}
                    />
                    <span>
                      <span className={styles.optionTitle}>{item.title}</span>
                      <span className={styles.optionDesc}>{item.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div
              className={cardClass("q9_subscribe_convince")}
              ref={(el) => setFieldRef("q9_subscribe_convince", el)}
            >
              <label className={styles.qLabel} htmlFor="survey-q9">
                {t.q9.label}
              </label>
              <textarea
                id="survey-q9"
                name="q9_subscribe_convince"
                placeholder={t.q9.placeholder}
                value={formValues.q9_subscribe_convince}
                onChange={handleChange}
              />
            </div>

            <div
              className={cardClass("q10_subscribe_likelihood")}
              ref={(el) => setFieldRef("q10_subscribe_likelihood", el)}
            >
              <label className={styles.qLabel}>{t.q10.label}</label>
              <div className={styles.scale}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <label key={n}>
                    <input
                      type="radio"
                      name="q10_subscribe_likelihood"
                      value={String(n)}
                      checked={formValues.q10_subscribe_likelihood === String(n)}
                      onChange={handleChange}
                    />
                    <span>{n}</span>
                  </label>
                ))}
              </div>
              <div className={styles.hint}>{t.scaleHint}</div>
            </div>

            <div className={styles.sectionTitle}>{t.sections.final}</div>

            <div
              className={cardClass("q11_nps")}
              ref={(el) => setFieldRef("q11_nps", el)}
            >
              <label className={styles.qLabel}>{t.q11.label}</label>
              <div className={styles.scale}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <label key={n}>
                    <input
                      type="radio"
                      name="q11_nps"
                      value={String(n)}
                      checked={formValues.q11_nps === String(n)}
                      onChange={handleChange}
                    />
                    <span>{n}</span>
                  </label>
                ))}
              </div>
              <div className={styles.hint}>{t.scaleHint}</div>
            </div>

            <div
              className={cardClass("q12_free_comment")}
              ref={(el) => setFieldRef("q12_free_comment", el)}
            >
              <label className={styles.qLabel} htmlFor="survey-q12">
                {t.q12.label}
              </label>
              <textarea
                id="survey-q12"
                name="q12_free_comment"
                value={formValues.q12_free_comment}
                onChange={handleChange}
              />
            </div>

            <div
              className={cardClass("consent")}
              ref={(el) => setFieldRef("consent", el)}
            >
              <label className={styles.consentRow}>
                <input
                  type="checkbox"
                  name="consent"
                  checked={formValues.consent}
                  onChange={handleChange}
                />
                <span>{t.consent}</span>
              </label>
            </div>

            <button
              type="submit"
              className={`${styles.submitBtn}${!isComplete || isSubmitting ? ` ${styles.inactive}` : ""}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? t.submitting : t.submit}
            </button>

            <div className={styles.clearWrap}>
              <button
                type="button"
                className={`${styles.clearBtn}${clearConfirming ? ` ${styles.clearBtnConfirm}` : ""}`}
                onClick={handleClear}
              >
                {clearConfirming ? t.clearConfirm : t.clear}
              </button>
            </div>

            {statusMessage && (
              <div className={styles.status} style={{ color: statusColor }}>
                {statusMessage}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default Survey;
